
/**
 * A simple hook to check a user's subscription plan
 */
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { UserType } from "@/types/user";
import { PLAN_LIMITS } from "@/hooks/useSubscriptionManager";
import { differenceInDays } from "date-fns";

export type UserPlanInfo = {
  planType: UserType;
  reportLimit: number;
  reportsRemaining: number | null;
  isLoading: boolean;
  nextResetDays: number | null;
};

/**
 * Hook for accessing user plan information
 * 
 * This hook provides:
 * 1. The user's current plan type ('free', 'starter', 'pro', 'premium')
 * 2. The report limit for their plan
 * 3. How many reports they have remaining
 * 4. Days until next reset (30-day rolling window)
 */
export const useUserPlan = (): UserPlanInfo => {
  const { user } = useAuth();
  const [planInfo, setPlanInfo] = useState<UserPlanInfo>({
    planType: 'free',
    reportLimit: PLAN_LIMITS.free,
    reportsRemaining: null,
    isLoading: true,
    nextResetDays: null,
  });

  useEffect(() => {
    const fetchUserPlan = async () => {
      if (!user) {
        setPlanInfo({
          planType: 'free',
          reportLimit: PLAN_LIMITS.free,
          reportsRemaining: 0,
          isLoading: false,
          nextResetDays: null,
        });
        return;
      }

      try {
        // Normalize email for consistent comparison
        const normalizedEmail = user.email?.trim().toLowerCase();
        
        // Debug log to help troubleshoot admin recognition
        console.log(`Email check in useUserPlan: '${normalizedEmail}'`);
        console.log(`Is admin email? ${normalizedEmail === 'kintupercy@gmail.com'}`);

        // Admin check - this user always gets unlimited reports
        if (normalizedEmail === 'kintupercy@gmail.com') {
          console.log('Admin email detected in useUserPlan, setting unlimited reports');
          setPlanInfo({
            planType: 'admin',
            reportLimit: 9999,
            reportsRemaining: 9999,
            isLoading: false,
            nextResetDays: null,
          });
          return;
        }

        // Check if reports need to be reset based on the 30-day window
        const checkForReportReset = async () => {
          const { data, error } = await supabase
            .from('report_usage')
            .select('last_reset_at')
            .eq('user_id', user.id)
            .single();
            
          if (error && error.code !== 'PGRST116') {
            console.error("Error checking last_reset_at:", error);
            return false;
          }
          
          if (!data || !data.last_reset_at) return false;
          
          const lastResetDate = new Date(data.last_reset_at);
          const today = new Date();
          const daysSinceReset = differenceInDays(today, lastResetDate);
          
          if (daysSinceReset >= 30) {
            // Reset reports
            const { error: resetError } = await supabase
              .from('report_usage')
              .update({
                reports_generated: 0,
                last_reset_at: today.toISOString(),
              })
              .eq('user_id', user.id);
              
            if (resetError) {
              console.error("Error resetting report usage:", resetError);
              return false;
            }
            
            console.log("Reset report count for user after 30 days");
            return true;
          }
          
          return false;
        };
        
        // Attempt to reset reports if needed
        await checkForReportReset();
        
        // Query the user_type column from profiles table
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('user_type')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error("Error fetching user profile:", profileError);
          return;
        }
        
        // Get the user's plan type from the profiles table
        // Normalize any non-valid types to 'free'
        let rawPlanType = profileData?.user_type as string || 'free';
        
        // Validate against allowed UserType values
        const validUserTypes: UserType[] = ['admin', 'free', 'starter', 'pro', 'premium'];
        let planType: UserType = 'free';
        
        if (validUserTypes.includes(rawPlanType as UserType)) {
          planType = rawPlanType as UserType;
        } else {
          console.log(`Invalid user_type "${rawPlanType}" found, normalizing to "free"`);
        }
        
        // Get the report limit for this plan
        const reportLimit = PLAN_LIMITS[planType] || PLAN_LIMITS.free;
        
        // Get the user's report usage to calculate remaining reports
        const { data: reportUsage, error: reportUsageError } = await supabase
          .from('report_usage')
          .select('reports_generated, last_reset_at')
          .eq('user_id', user.id)
          .single();
          
        if (reportUsageError && reportUsageError.code !== 'PGRST116') {
          console.error("Error fetching report usage:", reportUsageError);
        }
        
        // Calculate remaining reports
        const reportsUsed = reportUsage?.reports_generated || 0;
        const reportsRemaining = Math.max(0, reportLimit - reportsUsed);
        
        // Calculate days until the next reset (30 days from last_reset_at)
        let nextResetDays = null;
        if (reportUsage?.last_reset_at) {
          const lastResetDate = new Date(reportUsage.last_reset_at);
          const today = new Date();
          const daysSinceReset = differenceInDays(today, lastResetDate);
          nextResetDays = Math.max(0, 30 - daysSinceReset);
        }
        
        setPlanInfo({
          planType,
          reportLimit,
          reportsRemaining,
          isLoading: false,
          nextResetDays,
        });
      } catch (error) {
        console.error("Error in useUserPlan:", error);
        setPlanInfo({
          ...planInfo,
          isLoading: false,
        });
      }
    };

    fetchUserPlan();
    
    // Set up a real-time subscription to profile changes
    const profileSubscription = supabase
      .channel('profile-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user?.id}`,
        },
        () => {
          // Refresh user plan info when profile is updated
          fetchUserPlan();
        }
      )
      .subscribe();

    // Set up a real-time subscription to report usage changes
    const reportUsageSubscription = supabase
      .channel('report-usage-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'report_usage',
          filter: `user_id=eq.${user?.id}`,
        },
        () => {
          // Refresh user plan info when report usage changes
          fetchUserPlan();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(profileSubscription);
      supabase.removeChannel(reportUsageSubscription);
    };
  }, [user]);

  return planInfo;
};
